import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CabinetDetails,
  CreateCabinetFromCatalogRequest,
  CreateCabinetRequest,
  UpdateCabinetRequest,
} from '@kreart/shared';
import { invalidateProjectBom, projectsQueryKeys } from '@/features/projects/hooks';
import { cabinetsApi } from './api';

const ROOT = ['project-cabinets'] as const;
const CABINET_HARDWARE_ROOT = ['project-cabinet-hardware'] as const;
export const cabinetsQueryKeys = {
  all: ROOT,
  list: (projectId: string) => [...ROOT, 'list', projectId] as const,
  detail: (projectId: string, id: string) => [...ROOT, 'detail', projectId, id] as const,
};

export function useCabinets(projectId: string | undefined) {
  return useQuery({
    queryKey: cabinetsQueryKeys.list(projectId ?? 'nil'),
    queryFn: () => cabinetsApi.list(projectId!),
    enabled: Boolean(projectId),
    meta: { silent: true },
  });
}

export function useCabinet(projectId: string | undefined, id: string | undefined, enabled = true) {
  return useQuery({
    queryKey: cabinetsQueryKeys.detail(projectId ?? 'nil', id ?? 'nil'),
    queryFn: () => cabinetsApi.get(projectId!, id!),
    enabled: enabled && Boolean(projectId && id),
    meta: { silent: true },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>, projectId: string) {
  void qc.invalidateQueries({ queryKey: [...ROOT, 'list', projectId] });
  void qc.invalidateQueries({ queryKey: [...ROOT, 'detail', projectId] });
  void qc.invalidateQueries({ queryKey: [...CABINET_HARDWARE_ROOT, 'list', projectId] });
  // Project totals depend on line items.
  void qc.invalidateQueries({ queryKey: projectsQueryKeys.detail(projectId) });
  // BOM aggregates the cabinet's materials/edges/hardware.
  invalidateProjectBom(qc, projectId);
}

export function useCreateCabinet(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCabinetRequest) => cabinetsApi.create(projectId, body),
    meta: { silent: true },
    onSuccess: (_data: CabinetDetails) => invalidate(qc, projectId),
  });
}

export function useCreateCabinetFromCatalog(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCabinetFromCatalogRequest) =>
      cabinetsApi.createFromCatalog(projectId, body),
    meta: { silent: true },
    onSuccess: (_data: CabinetDetails) => invalidate(qc, projectId),
  });
}

export function useUpdateCabinet(projectId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCabinetRequest) => cabinetsApi.update(projectId, id, body),
    meta: { silent: true },
    onSuccess: (_data: CabinetDetails) => invalidate(qc, projectId),
  });
}

export function useDeleteCabinet(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => cabinetsApi.remove(projectId, id),
    meta: { silent: true },
    onSuccess: () => invalidate(qc, projectId),
  });
}
